package or.garden.manager.domain

import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.isSuccess
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import or.garden.manager.domain.GardenHttpClient.GARDEN_CLIENT
import or.garden.manager.domain.model.ApiResponse
import or.garden.manager.domain.model.NoteData
import or.garden.manager.domain.model.request.CreateNoteRequest

internal class NoteApi {
    suspend fun getNotes(): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get("${GardenHttpClient.baseUrl}/notes")
    }

    suspend fun createNote(title: String, content: String): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.post("${GardenHttpClient.baseUrl}/notes") {
            setBody(CreateNoteRequest(title, content))
        }
    }

    suspend fun updateNote(id: Int, title: String, content: String): Result<HttpResponse> =
        runCatching {
            return@runCatching GARDEN_CLIENT.put("${GardenHttpClient.baseUrl}/notes/$id") {
                setBody(
                    mapOf(
                        "title" to title,
                        "content" to content
                    )
                )
            }
        }

    suspend fun deleteNote(id: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.delete("${GardenHttpClient.baseUrl}/notes/$id")
    }
}

private data object NoteRepository {
    fun getNotes(): Flow<ResultState<ApiResponse<List<NoteData>>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = NoteApi()
                .getNotes()
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to load notes: ${e.message}"))
        }
    }

    fun createNote(title: String, content: String): Flow<ResultState<ApiResponse<NoteData>>> =
        flow {
            emit(ResultState.Loading)
            try {
                val response = NoteApi()
                    .createNote(title, content)
                    .getOrNull()
                if (response != null && response.status.isSuccess()) {
                    emit(ResultState.Success(response.body()))
                } else {
                    emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
                }
            } catch (e: Exception) {
                emit(ResultState.Error("Failed to create note: ${e.message}"))
            }
        }

    fun updateNote(
        id: Int,
        title: String,
        content: String
    ): Flow<ResultState<ApiResponse<NoteData>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = NoteApi()
                .updateNote(id, title, content)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to update note: ${e.message}"))
        }
    }

    fun deleteNote(id: Int): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        try {
            val response = NoteApi()
                .deleteNote(id)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to delete note: ${e.message}"))
        }
    }
}

data object NoteService {
    fun getNotes(result: (ResultState<ApiResponse<List<NoteData>>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            NoteRepository
                .getNotes()
                .collect {
                    result(it)
                }
        }
    }

    fun createNote(
        title: String,
        content: String,
        result: (ResultState<ApiResponse<NoteData>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            NoteRepository
                .createNote(title, content)
                .collect {
                    result(it)
                }
        }
    }

    fun updateNote(
        id: Int,
        title: String,
        content: String,
        result: (ResultState<ApiResponse<NoteData>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            NoteRepository
                .updateNote(id, title, content)
                .collect {
                    result(it)
                }
        }
    }

    fun deleteNote(id: Int, result: (ResultState<Unit>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            NoteRepository
                .deleteNote(id)
                .collect {
                    result(it)
                }
        }
    }
}
